import { Reducer } from "react";
import { Action } from "redux";
import { CommunicationChannel, InvoiceCatalogData, PackSettings, TaxRate, TaxExemption } from "../../models";
import { CatalogCategoryData, ReportTemplateData } from "../../models/api-models";
import { SurveySettingsData } from "../../shared/surveys";
import { SettingsState } from "./state";
import Actions from '../actions';
import { SettingsAction } from "./types";

const initialState: SettingsState = {
    appVersion: 'unkown',
    general: {
        openningHour: '07:00',
        closingHour: '21:00',
        countryCode: 'pt-PT',
        timeZone: 'GMT Standard Time',
        contacts: {},
        services: {},
        rooms: {},
        clientOrigins: [],
        clientSubsystems: [],
        paymentMethods: [],
        clientTags: {},
        expenseTypes: [],
        expenseEntities: [],
        stockCategories: [],
        appointmentChangeReasons: [],
    },
    entities: {
        pageSize: 500,
    },
    extras: {
        extras: [],
    },
    reports: { documents: [] },
    reminders: {
        appointmentConfirmation: {
            channels: [],
            name: '',
            message: '',
            hoursBeforeAppointment: 24,
            responses: {
                confirm: [],
                reject: [],
            },
        },
        smsHoursBeforeAppointment: [],
        emailHoursBeforeAppointment: [],
        whatsAppHoursBeforeAppointment: [],
        smsOnAppointmentCreation: false,
        emailOnAppointmentCreation: false,
        whatsAppOnAppointmentCreation: false,
    },
    taskManagement: {
        enabled: false,
        clientsFollowUp: {
            enabled: false,
            daysAfterFirstAppointment: 1,
        },
    },
    alerts: {},
    payments: {
        profiles: [],
    },
    invoices: {
        sendInvoicesAutomaticallyToClientsByEmail: false,
        sendSaftReportAutomaticallyToAt: false,
        emitInvoiceAutomaticallyOnPaymentConfirmation: false,
        addAppointmentPackDetailsAutomaticallyOnInvoice: false,
        companyLegalInfo: {},
    },
    aiPoweredRequests: {
        reports: {
            totalAvailableQuota: 5,
            left: 5,
            lastResetUtc: '0001-01-01T00:00:00',
        },
        surveys: {
            totalAvailableQuota: 5,
            left: 5,
            lastResetUtc: '0001-01-01T00:00:00',
        },
    },
    consentManagement: {
        consentsCheckboxes: [],
    },
    clientsLogin: {
        enabled: false,
    },
    integrations: {},
    cashRegisterSettings: {
        enabled: false,
        allPaymentTypes: true,
        paymentTypes: [],
        nonAdminApprovers: [],
        sharedRegisterNames: [],
    },
    waitingListSettings: {
        removeFromWaitingListWhenAppointmentCreated: true,
    },
    prescriptionsSettings: undefined,
    invoiceCatalog: undefined,
    invoiceCatalogLoading: false,
    taxRates: undefined,
    taxRatesLoading: false,
    taxExemptions: undefined,
    taxExemptionsLoading: false,
    reportTemplates: undefined,
    reportTemplatesLoading: false,
    reportTemplatesFetchedAt: undefined,
    packTemplates: undefined,
    packTemplatesLoading: false,
    packTemplatesFetchedAt: undefined,
    surveys: undefined,
    surveysLoading: false,
    reportTemplateCatalog: undefined,
    reportTemplateCatalogLoading: false,
    reportTemplateCatalogFetchedAt: undefined,
}

export const reconsilor = (state: SettingsState): SettingsState => ({
    ...initialState,
    ...state,
    appVersion: '<login again to refresh>',
});

export const reducer: Reducer<SettingsState | undefined, Action<Actions>> = (state = initialState, action): SettingsState => {
    switch (action.type) {
        case Actions.LoggedOut:
            return initialState;
        case Actions.SettingsLoaded:
        case Actions.SettingsUpdated:
            return handleSettingsUpdated(state, action as SettingsAction);
        case Actions.InvoiceCatalogLoading:
            return {
                ...state,
                invoiceCatalogLoading: true,
            };
        case Actions.InvoiceCatalogLoaded:
            return {
                ...state,
                invoiceCatalogLoading: false,
                invoiceCatalog: (action as InvoiceCatalogLoadedAction).payload,
            };
        case Actions.InvoiceCatalogCleared:
            return {
                ...state,
                invoiceCatalog: undefined,
                invoiceCatalogLoading: false,
            };
        case Actions.TaxRatesLoading:
            return {
                ...state,
                taxRatesLoading: true,
            };
        case Actions.TaxRatesLoaded:
            return {
                ...state,
                taxRatesLoading: false,
                taxRates: (action as TaxRatesLoadedAction).payload,
            };
        case Actions.TaxExemptionsLoading:
            return {
                ...state,
                taxExemptionsLoading: true,
            };
        case Actions.TaxExemptionsLoaded:
            return {
                ...state,
                taxExemptionsLoading: false,
                taxExemptions: (action as TaxExemptionsLoadedAction).payload,
            };
        case Actions.ReportTemplatesLoading:
            return {
                ...state,
                reportTemplatesLoading: true,
            };
        case Actions.ReportTemplatesLoaded: {
            const a = action as ReportTemplatesLoadedAction;
            return {
                ...state,
                reportTemplatesLoading: false,
                reportTemplates: a.payload.templates,
                reportTemplatesFetchedAt: a.payload.fetchedAt,
            };
        }
        case Actions.ReportTemplateVersionLoaded: {
            const template = (action as ReportTemplateVersionLoadedAction).payload;
            const already = state.reportTemplates?.some(
                t => t.templateId === template.templateId && t.version === template.version
            );
            return already ? state : {
                ...state,
                reportTemplates: [...(state.reportTemplates ?? []), template],
            };
        }
        case Actions.PackTemplatesLoading:
            return {
                ...state,
                packTemplatesLoading: true,
            };
        case Actions.PackTemplatesLoaded: {
            const a = action as PackTemplatesLoadedAction;
            return {
                ...state,
                packTemplatesLoading: false,
                packTemplates: a.payload.templates,
                packTemplatesFetchedAt: a.payload.fetchedAt,
            };
        }
        case Actions.PackTemplateVersionLoaded: {
            const template = (action as PackTemplateVersionLoadedAction).payload;
            const already = state.packTemplates?.some(
                t => t.templateId === template.templateId && t.version === template.version
            );
            return already ? state : {
                ...state,
                packTemplates: [...(state.packTemplates ?? []), template],
            };
        }
        case Actions.SurveysLoading:
            return {
                ...state,
                surveysLoading: true,
            };
        case Actions.SurveysLoaded:
            return {
                ...state,
                surveysLoading: false,
                surveys: (action as SurveysLoadedAction).payload,
            };
        case Actions.ReportTemplateCatalogLoading:
            return {
                ...state,
                reportTemplateCatalogLoading: true,
            };
        case Actions.ReportTemplateCatalogLoaded: {
            const a = action as ReportTemplateCatalogLoadedAction;
            return {
                ...state,
                reportTemplateCatalogLoading: false,
                reportTemplateCatalog: a.payload.catalog,
                reportTemplateCatalogFetchedAt: a.payload.fetchedAt,
            };
        }
        default:
            return state;
    }
};

const handleSettingsUpdated = (state: SettingsState, action: SettingsAction): SettingsState => {
    const payload = action.payload;
    const mergedAppointmentConfirmation = payload?.reminders?.appointmentConfirmation
        ? {
            ...state.reminders.appointmentConfirmation,
            ...payload.reminders.appointmentConfirmation,
            responses: {
                ...(state.reminders.appointmentConfirmation?.responses ?? {}),
                ...(payload.reminders.appointmentConfirmation.responses ?? {}),
            },
            channels: payload.reminders.appointmentConfirmation.channels
                ?? state.reminders.appointmentConfirmation?.channels
                ?? [],
            hoursBeforeAppointment: payload.reminders.appointmentConfirmation.hoursBeforeAppointment
                ?? state.reminders.appointmentConfirmation?.hoursBeforeAppointment
                ?? 24,
        }
        : state.reminders.appointmentConfirmation;

    const mergedReminders = payload?.reminders
        ? {
            ...state.reminders,
            ...payload.reminders,
            appointmentConfirmation: mergedAppointmentConfirmation,
        }
        : state.reminders;

    return {
        ...state,
        ...payload,
        reminders: mergedReminders,
        invoiceCatalog: undefined,
    };
};

interface InvoiceCatalogLoadedAction extends Action<Actions> {
    payload?: InvoiceCatalogData,
}

interface TaxRatesLoadedAction extends Action<Actions> {
    payload?: TaxRate[],
}

interface TaxExemptionsLoadedAction extends Action<Actions> {
    payload?: TaxExemption[],
}

interface ReportTemplatesLoadedAction extends Action<Actions> {
    payload: { templates: ReportTemplateData[], fetchedAt: number },
}

interface ReportTemplateVersionLoadedAction extends Action<Actions> {
    payload: ReportTemplateData,
}

interface PackTemplatesLoadedAction extends Action<Actions> {
    payload: { templates: PackSettings[], fetchedAt: number },
}

interface PackTemplateVersionLoadedAction extends Action<Actions> {
    payload: PackSettings,
}

interface SurveysLoadedAction extends Action<Actions> {
    payload: SurveySettingsData[],
}

interface ReportTemplateCatalogLoadedAction extends Action<Actions> {
    payload: { catalog: CatalogCategoryData[], fetchedAt: number },
}
